// import creditsIcon from "../../assets/icons/peppa_coin.png";
//import { Alert, AlertDescription } from "../../../components/ui/alert";
import { useNavigate } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import DataService from "../../components/auth/Service";
import { toast } from "sonner";
import { trackEvent, trackOncePerSession } from "../../lib/matomo";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import peppaLogo from "../../assets/icons/peppa_logo_blue.svg";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";
import { ThemeToggle } from "../../components/ui/theme-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

import { useState, useEffect, useCallback } from "react";
import { Plus, Send, User, Trash2, BellRing, Menu, X, CreditCard, ChevronDown, LogOut, XCircle, Check, Search, CircleChevronLeft } from "lucide-react";
import NotificationService from "./NotificationService";
import { useUser } from "../../context/UserContext";

const capitalizeWords = (str) => {
  return str
    .split(" ")
    .map(word =>
      word
        .split("-")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join("-")
    )
    .join(" ");
};

const transformAPIDataToOptions = (apiData) => {
  return apiData.map((item, index) => ({
    id: index + 1,
    name: capitalizeWords(item)
  }));
};

const SearchableMultiSelect = ({ label, options, selectedIds, onToggle, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedItems = options.filter(opt => selectedIds.includes(opt.id));

  // Filtrer les options en fonction de la recherche
  const filteredOptions = options.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Limiter l'affichage pour les performances
  const displayedOptions = filteredOptions.slice(0, 100);

  return (
    <div className="relative">
      <Label className="text-foreground">{label}</Label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="mt-2 w-full rounded-md border border-border/50 bg-card/90 backdrop-blur-sm px-3 py-2 text-left text-sm text-foreground hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {selectedItems.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selectedItems.map(item => (
                <Badge key={item.id} variant="secondary" className="inline-flex items-center gap-1">
                  {item.name}
                  <XCircle
                    className="w-3 h-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(item.id);
                    }}
                  />
                </Badge>
              ))}
            </div>
          )}
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => {
                setIsOpen(false);
                setSearchTerm("");
              }}
            />
            <div className="absolute z-20 mt-1 w-full rounded-md border border-border/50 bg-card/95 backdrop-blur-sm shadow-lg">
              {/* Barre de recherche */}
              <div className="p-2 border-b border-border/40">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-border/50 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Liste des résultats */}
              <div className="max-h-60 overflow-auto">
                {displayedOptions.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                    Aucun résultat trouvé
                  </div>
                ) : (
                  <>
                    {displayedOptions.map(option => {
                      const isSelected = selectedIds.includes(option.id);
                      return (
                        <div
                          key={option.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggle(option.id);
                          }}
                          className={`px-3 py-2 cursor-pointer hover:bg-accent/40 flex items-center justify-between ${isSelected ? 'bg-accent/30' : ''
                            }`}
                        >
                          <span className="text-sm text-foreground">{option.name}</span>
                          {isSelected && <Check className="w-4 h-4 text-primary" />}
                        </div>
                      );
                    })}
                    {filteredOptions.length > 100 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t border-border/40">
                        {filteredOptions.length - 100} résultats supplémentaires... Affinez votre recherche.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const MultiSelect = ({ label, options, selectedIds, onToggle, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedItems = options.filter(opt => selectedIds.includes(opt.id));

  return (
    <div className="relative">
      <Label className="text-foreground">{label}</Label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="mt-2 w-full rounded-md border border-border/50 bg-card/90 backdrop-blur-sm px-3 py-2 text-left text-sm text-foreground hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {selectedItems.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selectedItems.map(item => (
                <Badge key={item.id} variant="secondary" className="inline-flex items-center gap-1">
                  {item.name}
                  <XCircle
                    className="w-3 h-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(item.id);
                    }}
                  />
                </Badge>
              ))}
            </div>
          )}
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute z-20 mt-1 w-full rounded-md border border-border/50 bg-card/95 backdrop-blur-sm shadow-lg max-h-60 overflow-auto">
              {options.map(option => {
                const isSelected = selectedIds.includes(option.id);
                return (
                  <div
                    key={option.id}
                    onClick={() => onToggle(option.id)}
                    className={`px-3 py-2 cursor-pointer hover:bg-accent/40 flex items-center justify-between ${isSelected ? 'bg-accent/30' : ''
                      }`}
                  >
                    <span className="text-sm text-foreground">{option.name}</span>
                    {isSelected && <Check className="w-4 h-4 text-primary" />}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function Notification() {
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoaded, setNotificationsLoaded] = useState(true);
  const [activeNotificationId, setActiveNotificationId] = useState(null);
  // Gestion intelligente de la sidebar (même logique que ChatModern / CopilotModern)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const mobile = window.innerWidth < 1024;
    if (mobile) return false;
    const savedState = localStorage.getItem("chat-sidebar-open");
    return savedState !== null ? JSON.parse(savedState) : true;
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [showCreationPanel, setShowCreationPanel] = useState(false);

  // États pour le formulaire de création
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedLocalities, setSelectedLocalities] = useState([]);
  const [selectedCallTypes, setSelectedCallTypes] = useState([]);
  const [selectedCompanyTypes, setSelectedCompanyTypes] = useState([]);

  const { user, resetUserInfo } = useUser();
  const navigate = useNavigate();
  const displayName = user ? user.first_name : null;

  useEffect(() => {
    trackOncePerSession("notification_opened", () => trackEvent("notification", "opened"));
  }, []);


  const [all_Localities, setAll_Localities] = useState([]);
  const [all_CallTypes, setAll_CallTypes] = useState([]);
  const [all_CompanyTypes, setAll_CompanyTypes] = useState([]);


  const fetch_all_filters = useCallback(async () => {
    try {
      const localitiesRes = await NotificationService.getLocalities();
      setAll_Localities(transformAPIDataToOptions(localitiesRes.data));
      const callTypesRes = await NotificationService.getTypeOfCalls();
      setAll_CallTypes(transformAPIDataToOptions(callTypesRes.data));
      const companyTypesRes = await NotificationService.getTypeOfCompanies();
      setAll_CompanyTypes(transformAPIDataToOptions(companyTypesRes.data));

    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  const fetch_all_notifications = useCallback(async () => {
    setNotificationsLoaded(false);
    try {
      const notificationsRes = await NotificationService.getNotification();
      setNotifications(notificationsRes.data);
      setNotificationsLoaded(true);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  useEffect(() => {
    fetch_all_filters();
    fetch_all_notifications();
  }, [fetch_all_filters, fetch_all_notifications]);

  // Sauvegarder l'état de la sidebar uniquement sur desktop (clé partagée)
  useEffect(() => {
    const mobile = window.innerWidth < 1024;
    if (!mobile) {
      localStorage.setItem("chat-sidebar-open", JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen]);

  // Détecter les changements de taille d'écran (auto-close mobile, restore desktop)
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      if (mobile) {
        setSidebarOpen(false);
      } else {
        const savedState = localStorage.getItem("chat-sidebar-open");
        setSidebarOpen(savedState !== null ? JSON.parse(savedState) : true);
      }
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarOpen]);

  const handleNewNotification = () => {
    try { trackEvent("notification", "create_clicked"); } catch (_) {}
    setShowCreationPanel(true);
    setActiveNotificationId(null);
    // Réinitialiser le formulaire
    setTitle("");
    setDescription("");
    setSelectedLocalities([]);
    setSelectedCallTypes([]);
    setSelectedCompanyTypes([]);
  };

  const handleSelectNotification = (notificationId) => {
    try { trackEvent("notification", "selected"); } catch (_) {}
    setActiveNotificationId(notificationId);
    setShowCreationPanel(false);
  };

  const handleSubmitNotification = () => {
    if (!title.trim() || !description.trim() || selectedLocalities.length === 0 || selectedCallTypes.length === 0 || selectedCompanyTypes.length === 0) {
      toast.error("Veuillez remplir tous les champs du formulaire.");
      try { trackEvent("notification", "create_validation_error"); } catch (_) {}
      return;
    }

    const localitiesString = all_Localities
      .filter(loc => selectedLocalities.includes(loc.id))
      .map(loc => loc.name)
      .join(", ");

    const callTypesString = all_CallTypes
      .filter(ct => selectedCallTypes.includes(ct.id))
      .map(ct => ct.name)
      .join(", ");

    const companyTypesString = all_CompanyTypes
      .filter(ct => selectedCompanyTypes.includes(ct.id))
      .map(ct => ct.name)
      .join(", ");


    const newNotification = {
      user_id: null,
      title: title,
      description: description,
      locality: localitiesString,
      type_of_call: callTypesString,
      type_of_company: companyTypesString,
      sent_at: null
    };

    NotificationService.postNotification(newNotification)
      .then(response => {
        console.log("Notification créée avec succès :", response.data);
        setNotifications(prev => [...prev, response.data]);
        setShowCreationPanel(false);
        toast.success("Notification créée !");
        try { trackEvent("notification", "notification_created"); } catch (_) {}
      })
      .catch(error => {
        console.log("Erreur lors de la création de la notification :", error);
        if (error.response && error.response.status === 402) {
          toast.error("Il faut un abonnement pour créer une notification.");
          try { trackEvent("notification", "create_blocked_no_subscription"); } catch (_) {}
          return;
        }
        console.error("Erreur lors de la création de la notification :", error);
        toast.error("Une erreur est survenue lors de la création de la notification.");
        try { trackEvent("notification", "create_error"); } catch (_) {}
      });
    // Pour la démo, on affiche un message
  };

  const handleClickLogOut = async (e) => {
    e.preventDefault();
    try {
      await DataService.logout();
      resetUserInfo();
      navigate(RouterPath.LOGIN);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleLocality = (id) => {
    setSelectedLocalities(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleCallType = (id) => {
    setSelectedCallTypes(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleCompanyType = (id) => {
    setSelectedCompanyTypes(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const getNotificationTitle = (notification) => {
    return notification.title || "Notification";
  };

  const confirmDeleteNotification = (notification) => {
    try { trackEvent("notification", "delete_clicked"); } catch (_) {}
    setNotificationToDelete(notification);
    // const confirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer la notification "${getNotificationTitle(notification)}" ?`);
    // if (confirmed) {
    //   NotificationService.deleteNotification(notification.id)
    //     .then(response => {
    //       if (response.data > 0) {  
    //         fetch_all_notifications();
    //         if (activeNotificationId === notification.id) {
    //           setActiveNotificationId(null);
    //         }
    //         toast.success("Notification supprimée avec succès.");
    //       } else {
    //         toast.error("Vous ne pouvez pas supprimer cette notification.");
    //       }
    //     })
    //     .catch(error => {
    //       console.error("Erreur lors de la suppression de la notification :", error);
    //       toast.error("Une erreur est survenue lors de la suppression de la notification.");
    //     });
    // }
  };

  const handleDelete = (notificationId) => {
    NotificationService.deleteNotification(notificationId)
      .then(response => {
        if (response.data > 0) {
          fetch_all_notifications();
          if (activeNotificationId === notificationId) {
            setActiveNotificationId(null);
          }
          toast.success("Notification supprimée avec succès.");
          try { trackEvent("notification", "deleted"); } catch (_) {}
        } else {
          toast.error("Vous ne pouvez pas supprimer cette notification.");
          try { trackEvent("notification", "delete_forbidden"); } catch (_) {}
        }
      })
      .catch(error => {
        console.error("Erreur lors de la suppression de la notification :", error);
        toast.error("Une erreur est survenue lors de la suppression de la notification.");
        try { trackEvent("notification", "delete_error"); } catch (_) {}
      });
    setNotificationToDelete(null);
  };


  return (
    <div className="flex h-screen relative app-page-gradient">
      {/* Mobile overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile menu button */}
      {isMobile && !sidebarOpen && (
        <Button
          onClick={() => setSidebarOpen(true)}
          variant="secondary"
          size="sm"
          title="Ouvrir la barre latérale"
          className="fixed top-4 left-4 z-50 shadow-lg border-0 bg-card/90 backdrop-blur-sm hover:bg-card text-foreground"
        >
          <Menu className="w-4 h-4" />
        </Button>
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed lg:relative z-40 transition-all duration-300 ease-in-out bg-card/95 backdrop-blur-sm flex flex-col overflow-hidden h-full",
        sidebarOpen ? 'w-60 translate-x-0' : 'w-60 -translate-x-full lg:translate-x-0 lg:w-16',
        "lg:border-r lg:border-border/30"
      )}>
        {/* Sidebar Header */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={peppaLogo} alt="PeppaCorp" className="w-40 rounded-sm" />
              </div>
              {/* Desktop collapse button */}
              <Button
                onClick={() => setSidebarOpen(false)}
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex hover:bg-accent/50 text-foreground"
                title="Replier la barre latérale"
                aria-label="Replier la barre latérale"
              >
                <Menu className="w-4 h-4" />
              </Button>
              {/* Close button for mobile */}
              <Button
                onClick={() => setSidebarOpen(false)}
                variant="ghost"
                size="sm"
                className="lg:hidden hover:bg-accent/50 text-foreground"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              {/* Single button: logo becomes hamburger on hover */}
              <button
                aria-label="Ouvrir la barre latérale"
                title="Ouvrir la barre latérale"
                className="group relative rounded hover:bg-accent/40 text-foreground"
                onClick={() => setSidebarOpen(true)}
              >
                <img
                  src="/favicon.svg"
                  alt="PeppaCorp"
                  className="h-6 w-6 rounded transition-opacity duration-150 group-hover:opacity-0"
                />
                <Menu className="absolute inset-0 m-auto h-4 w-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
              </button>
            </div>
          )}
        </div>

        {/* Retour au chat */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <Button
              onClick={() => navigate(RouterPath.CHAT)}
              className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
              variant="ghost"
              size="sm"
            >
              <CircleChevronLeft className="w-4 h-4" />
              <span>Retour</span>
            </Button>
          ) : (
            <div className="flex items-center justify-center">
              <Button
                onClick={() => navigate(RouterPath.CHAT)}
                variant="ghost"
                size="icon"
                title="Retour"
                className="h-8 w-8 hover:bg-accent/50 text-foreground"
                aria-label="Retour"
              >
                <CircleChevronLeft className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* New notification button */}
        {/* <div className="p-3 border-b border-gray-200 dark:border-gray-800"> */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <Button
              onClick={handleNewNotification}
              // className="flex items-center gap-2 w-full justify-start"
              className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4" />
              <span>Nouvelle veille</span>
            </Button>
          ) : (
            <Button
              onClick={handleNewNotification}
              className="hover:bg-accent/50 text-foreground"
              variant="ghost"
              size="icon"
              title="Nouvelle demande de veille"
              aria-label="Nouvelle demande de veille"
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto p-2 sidebar-scroll">
          {/* Cache la liste quand la sidebar est repliée sur desktop */}
          {!sidebarOpen && !isMobile ? null : !notificationsLoaded ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <BellRing className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune demande de veille</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.slice().reverse().map((notif) => (
                <Card
                  key={notif.id}
                  className={`cursor-pointer transition-all duration-200 group ${activeNotificationId === notif.id
                    ? "bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500/20"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  onClick={() => handleSelectNotification(notif.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${activeNotificationId === notif.id
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-900 dark:text-gray-100"
                          }`}>
                          {getNotificationTitle(notif)}
                        </p>
                      </div>
                      {/* <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteNotification(notif);
                        }}
                      >
                        <Trash2 className="w-3 h-3" /> */}
                      {/* </Button> */}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-border/20">
          <div className="flex items-center justify-between mb-3">
            <ThemeToggle compact={!sidebarOpen} />
            {/* {sidebarOpen && (
              <Badge variant="secondary" className="text-xs bg-accent/50 text-foreground border-0 inline-flex items-center gap-1">
                {user?.credits ?? 0}
                <img src={creditsIcon} alt="crédits" className="inline-block w-3 h-3" />
              </Badge>
            )} */}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={cn("w-full hover:bg-accent/50 text-foreground", (!sidebarOpen) ? "px-0 justify-center" : "justify-start")}>
                <User className={cn("h-4 w-4", (!sidebarOpen) ? "" : "mr-2")} />
                {sidebarOpen && (
                  <>
                    <span className="truncate text-foreground">
                      {displayName || "Utilisateur"}
                    </span>
                    <ChevronDown className="h-4 w-4 ml-auto" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(RouterPath.PROFILE)}>
                <User className="mr-2 h-4 w-4" />
                <span>Mon profil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(RouterPath.BILLING)}>
                <CreditCard className="mr-2 h-4 w-4" />
                <span className="inline-flex items-center gap-1">Abonnement</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleClickLogOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Se déconnecter</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col relative">
        {showCreationPanel ? (
          <div className="h-full flex items-center justify-center p-4">
            <Card className="glass-card border-muted shadow-2xl w-full max-w-2xl bg-card/80">
              <CardContent className="p-6 space-y-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">
                    Nouvelle demande de veille
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreationPanel(false)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Titre */}
                  <div>
                    <Label className="text-foreground">Titre *</Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Entrez le titre de la demande de veille"
                      className="mt-2"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-foreground">Description *</Label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Décrivez votre projet afin d’identifier les aides pertinentes..."
                      rows={4}
                      className="mt-2 border-border/50 bg-background text-foreground focus-visible:ring-ring"
                    />
                  </div>

                  {/* Localités */}
                  <SearchableMultiSelect
                    label="Localités"
                    options={all_Localities}
                    selectedIds={selectedLocalities}
                    onToggle={toggleLocality}
                    placeholder="Sélectionnez les territoires couverts par la veille"
                  />

                  {/* Types d'appels */}
                  <MultiSelect
                    label="Types d'appels"
                    options={all_CallTypes}
                    selectedIds={selectedCallTypes}
                    onToggle={toggleCallType}
                    placeholder="Sélectionnez un ou plusieurs types d'aides"
                  />

                  {/* Types d'organismes bénéficiaires */}
                  <MultiSelect
                    label="Types d'organismes bénéficiaires"
                    options={all_CompanyTypes}
                    selectedIds={selectedCompanyTypes}
                    onToggle={toggleCompanyType}
                    placeholder="Sélectionnez un ou plusieurs types d'organismes bénéficiaires"
                  />

                  {/* Boutons d'action */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleSubmitNotification}
                      className="flex-1"
                      disabled={!title || !description}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Créer la demande de veille
                    </Button>
                    <Button
                      onClick={() => setShowCreationPanel(false)}
                      variant="ghost"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeNotificationId ? (
          <div className="h-full flex items-center justify-center p-4 overflow-y-auto">
            <Card className="w-full max-w-2xl shadow-xl my-4">
              <CardContent className="p-6">
                {(() => {
                  const notification = notifications.find(n => n.id === activeNotificationId);
                  if (!notification) return <p>Notification non trouvée</p>;

                  return (
                    <>
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {notification.title}
                        </h2>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setActiveNotificationId(null)}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>

                      <div className="space-y-6">
                        {/* Description */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Description
                          </h3>
                          <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
                            {notification.description}
                          </p>
                        </div>

                        {/* Localités */}
                        {notification.locality && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Localités ciblées
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {notification.locality.split(", ").map((loc, idx) => (
                                <Badge key={idx} variant="secondary">
                                  {loc}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Types d'appels */}
                        {notification.type_of_call && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Types d'aides
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {notification.type_of_call.split(", ").map((type, idx) => (
                                <Badge key={idx} variant="default">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Types d'entreprises */}
                        {notification.type_of_company && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Organismes bénéficiaires
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {notification.type_of_company.split(", ").map((type, idx) => (
                                <Badge key={idx} variant="default">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Date d'envoi */}
                        {notification.sent_at && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Envoyée le
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                              {new Date(notification.sent_at).toLocaleString('fr-FR', {
                                dateStyle: 'long',
                                timeStyle: 'short'
                              })}
                            </p>
                          </div>
                        )}

                        {/* Bouton de suppression */}
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                          <Button
                            onClick={() => confirmDeleteNotification(notification)}
                            variant="destructive"
                            className="w-full"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer cette demande de veille
                          </Button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        ) : (
          // <div className="h-full flex items-center justify-center p-4 max-w-3xl mx-auto">
          <div className="flex flex-col items-center justify-center h-full text-center p-4 max-w-3xl mx-auto">
            {/* <div className="text-center"> */}
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <BellRing className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-3">
              Veille intelligente <span className="text-primary relative">automatisée
                <Badge variant="secondary" className="absolute -top -right-5 pointer-events-none">
                  beta
                </Badge>
              </span>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Une demande de veille permet à PeppaCorp de surveiller en continu les aides correspondant à vos critères et de vous envoyer un récapitulatif personnalisé chaque semaine par email.
            </p>
            <Button onClick={handleNewNotification}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle demande de veille
            </Button>
            {/* </div> */}
          </div>
        )}
      </div>
      <Dialog
        open={!!notificationToDelete}
        onOpenChange={() => setNotificationToDelete(null)}
      >
        <DialogContent className="sm:max-w-[400px] bg-background text-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Supprimer la demande de veille</DialogTitle>
            <DialogDescription>
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setNotificationToDelete(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleDelete(notificationToDelete.id);
                setNotificationToDelete(null);
              }}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}